import React, { useState } from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Button, Label, Spinner, TextInput } from "flowbite-react";
import { useAuthentication, useTranslation, useToast } from "@repo/core";
import SocialLoginButtons from "../components/auth/SocialLoginButtons";
import { isSocialLoginEnabled } from "../config/featureFlags";
import { extractErrorMessage } from "../utils/authErrors";

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { login, isAuthenticated } = useAuthentication();
  const { addErrorMessage } = useToast();
  const location = useLocation();
  const { t, tCommon } = useTranslation();

  // Get the page user was trying to access
  const from =
    ((location.state as { from?: { pathname: string } }) || {}).from
      ?.pathname || "/";

  // If already authenticated, redirect to intended page
  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'User clicked Sign In button',
      level: 'info',
    });

    try {
      await login(email, password);
      // Navigation will be handled automatically by the redirect above
    } catch (error) {
      console.error("Login failed:", error);
      addErrorMessage(extractErrorMessage(error) ?? t("login.loginFailed"));

      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Login attempt failed',
        level: 'warning',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-8 shadow dark:bg-gray-800">
        <h2 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">
          {t("login.title")}
        </h2>

        {isSocialLoginEnabled && (
          <>
            <SocialLoginButtons />

            <div className="my-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
              <span className="text-xs text-gray-400">{tCommon("orDivider")}</span>
              <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
            </div>
          </>
        )}

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <Label htmlFor="email">{t("login.emailLabel")}</Label>
            <TextInput
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              placeholder={t("login.emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <Label htmlFor="password">{t("login.passwordLabel")}</Label>
              <button
                type="button"
                className="text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                onClick={() => setShowPassword((show) => !show)}
              >
                {showPassword ? t("login.hidePassword") : t("login.showPassword")}
              </button>
            </div>
            <TextInput
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              required
              placeholder={t("login.passwordPlaceholder")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div className="text-right">
            <Link
              to="/forgot-password"
              className="text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              {t("login.forgotPassword")}
            </Link>
          </div>

          <Button type="submit" color="red" className="w-full" disabled={loading}>
            {loading && <Spinner size="sm" className="mr-2" light />}
            {loading ? t("login.signingIn") : t("login.signIn")}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          {t("login.noAccount")}{" "}
          <Link to="/signup" className="font-medium text-blue-600 hover:underline dark:text-blue-400">
            {t("login.createAccount")}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
